import { z } from "zod";
/**
 * Ideation - premium knowledge-graph read model over Notes.
 *
 * The source of truth stays in `notes` and `note_links`; this schema
 * describes the graph projection consumed by the staff Ideation blade.
 */
export const IdeationNodeKindSchema = z.enum(["note", "concept", "person", "entity"]);
export const IdeationEdgeKindSchema = z.enum(["wikilink", "mention", "tag", "author"]);
export const IdeationGraphQuerySchema = z.object({
    search: z.string().min(1).max(200).optional(),
    tag: z.string().min(1).max(60).optional(),
    limit: z.coerce.number().int().min(10).max(200).default(80),
});
export const IdeationGraphNodeSchema = z.object({
    id: z.string().min(1),
    kind: IdeationNodeKindSchema,
    label: z.string().min(1).max(300),
    detail: z.string().max(500).nullable(),
    href: z.string().nullable(),
    weight: z.number().int().min(1),
});
export const IdeationGraphEdgeSchema = z.object({
    id: z.string().min(1),
    source: z.string().min(1),
    target: z.string().min(1),
    kind: IdeationEdgeKindSchema,
    label: z.string().max(120).nullable(),
    weight: z.number().int().min(1),
});
export const IdeationGraphBriefSchema = z.object({
    generatedAt: z.string().datetime(),
    noteCount: z.number().int().min(0),
    conceptCount: z.number().int().min(0),
    contributorCount: z.number().int().min(0),
    edgeCount: z.number().int().min(0),
    topConcepts: z.array(z.object({
        label: z.string().min(1).max(120),
        count: z.number().int().min(1),
    })),
    suggestedPrompts: z.array(z.string().min(1).max(220)),
});
export const IdeationGraphResponseSchema = z.object({
    brief: IdeationGraphBriefSchema,
    nodes: z.array(IdeationGraphNodeSchema),
    edges: z.array(IdeationGraphEdgeSchema),
});
const nullableQuestionScopeText = (max) => z.preprocess((value) => {
    if (typeof value !== "string")
        return value;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
}, z.string().min(1).max(max).nullable().optional());
export const IdeationClaudeAskSchema = z.object({
    question: z.string().trim().min(1).max(2000),
    search: nullableQuestionScopeText(200),
    tag: nullableQuestionScopeText(60),
    limit: z.number().int().min(10).max(200).default(120),
    selectedNodeId: nullableQuestionScopeText(300),
});
export const IdeationClaudeSourceRefSchema = z.object({
    source: z.literal("ideation"),
    kind: IdeationNodeKindSchema,
    nodeId: z.string().min(1).max(300),
    label: z.string().min(1).max(300),
    href: z.string().max(1000).nullable(),
    detail: z.string().max(500).nullable(),
});
export const IdeationClaudeResponseSchema = z.object({
    answer: z.string().min(1).max(12_000),
    sourceRefs: z.array(IdeationClaudeSourceRefSchema).max(12),
    generatedAt: z.string().datetime(),
});
const nullableScopedText = (max) => z.preprocess((value) => {
    if (typeof value !== "string")
        return value;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
}, z.string().min(1).max(max).nullable().optional());
const QueryBooleanSchema = z.preprocess((value) => {
    if (value === undefined || typeof value === "boolean")
        return value;
    if (typeof value !== "string")
        return value;
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1")
        return true;
    if (normalized === "false" || normalized === "0")
        return false;
    return value;
}, z.boolean().optional());
export const IdeationNodePositionSchema = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
});
export const IdeationBoardScopeSchema = z.object({
    search: z.string().min(1).max(200).nullable(),
    tag: z.string().min(1).max(60).nullable(),
    limit: z.number().int().min(10).max(200),
});
export const IdeationBoardSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(180),
    ownerEmail: z.string().email(),
    sharedWith: z.array(z.string().email()).max(50),
    search: z.string().min(1).max(200).nullable(),
    tag: z.string().min(1).max(60).nullable(),
    limit: z.number().int().min(10).max(200),
    pinnedNodeIds: z.array(z.string().min(1).max(300)).max(100),
    nodePositions: z.record(z.string().min(1), IdeationNodePositionSchema),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const IdeationBoardListQuerySchema = z.object({
    includeArchived: QueryBooleanSchema.default(false),
    archivedOnly: QueryBooleanSchema,
});
export const IdeationBoardListResponseSchema = z.object({
    boards: z.array(IdeationBoardSchema),
});
const IdeationBoardMutationBaseSchema = z.object({
    title: z.string().trim().min(1).max(180),
    sharedWith: z.array(z.string().trim().email()).max(50).default([]),
    search: nullableScopedText(200),
    tag: nullableScopedText(60),
    limit: z.number().int().min(10).max(200).default(120),
    pinnedNodeIds: z.array(z.string().min(1).max(300)).max(100).default([]),
    nodePositions: z.record(z.string().min(1), IdeationNodePositionSchema).default({}),
});
export const IdeationBoardCreateSchema = IdeationBoardMutationBaseSchema;
export const IdeationBoardUpdateSchema = IdeationBoardMutationBaseSchema.partial().refine((value) => Object.keys(value).length > 0, { message: "At least one field must be supplied" });
export const IdeationClusterStatusSchema = z.enum(["draft", "approved"]);
export const IdeationClusterSourceRefSchema = z.object({
    source: z.literal("ideation"),
    kind: IdeationNodeKindSchema,
    nodeId: z.string().min(1).max(300),
    label: z.string().min(1).max(300),
    href: z.string().max(1000).nullable(),
});
export const IdeationClusterSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(180),
    summary: z.string().min(1).max(4000),
    rationale: z.string().min(1).max(4000),
    status: IdeationClusterStatusSchema,
    search: z.string().min(1).max(200).nullable(),
    tag: z.string().min(1).max(60).nullable(),
    limit: z.number().int().min(10).max(200),
    nodeIds: z.array(z.string().min(1).max(300)).max(100),
    sourceRefs: z.array(IdeationClusterSourceRefSchema).max(20),
    generatedBy: z.string().email(),
    approvedAt: z.string().datetime().nullable(),
    approvedBy: z.string().email().nullable(),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const IdeationClusterListQuerySchema = z.object({
    includeArchived: QueryBooleanSchema.default(false),
    archivedOnly: QueryBooleanSchema,
    status: IdeationClusterStatusSchema.optional(),
});
export const IdeationClusterListResponseSchema = z.object({
    clusters: z.array(IdeationClusterSchema),
});
export const IdeationClusterGenerateSchema = z.object({
    search: nullableScopedText(200),
    tag: nullableScopedText(60),
    limit: z.number().int().min(10).max(200).default(120),
    maxClusters: z.number().int().min(1).max(6).default(4),
});
export const IdeationClusterGenerateResponseSchema = z.object({
    clusters: z.array(IdeationClusterSchema),
});
export const IdeationClusterUpdateSchema = z
    .object({
    title: z.string().trim().min(1).max(180).optional(),
    summary: z.string().trim().min(1).max(4000).optional(),
    rationale: z.string().trim().min(1).max(4000).optional(),
    nodeIds: z.array(z.string().min(1).max(300)).max(100).optional(),
    sourceRefs: z.array(IdeationClusterSourceRefSchema).max(20).optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one field must be supplied",
});
//# sourceMappingURL=ideation.js.map